import { isAxiosError } from 'axios';

/** Extract a user-friendly error message from an API failure. */
export function extractErrorMessage(err: unknown): string {
  // Axios errors (non-streaming fallback)
  if (isAxiosError(err)) {
    const status = err.response?.status;
    if (status === 401) return 'Session expired. Please refresh and try again.';
    if (status === 403) return 'You do not have permission to run queries.';
    if (status === 502) return 'The AI service is temporarily unavailable. Try again in a moment.';
    if (status === 503) {
      const detail = extractDetailMessage(err, '');
      if (detail) return friendlyErrorMessage(detail, false);
    }
    if (status && status >= 500) return 'Something went wrong on the server. Please try again.';
    if (err.code === 'ERR_NETWORK') return 'Network error — check your connection and try again.';
  }
  // Fetch/stream errors
  if (err instanceof Error) {
    if (err.message.includes('401')) return 'Session expired. Please refresh and try again.';
    if (err.message.includes('403')) return 'You do not have permission to run queries.';
    if (err.message.includes('Failed to fetch')) return 'Network error — check your connection and try again.';
  }
  return 'Query failed. Please try again.';
}

/**
 * The server's own words when a read was refused because the signed-in role may not see it
 * (XC-WHO-SEES-WHAT R1: a 403 saying "hidden for your role"); null for any other failure.
 */
export function hiddenForRoleMessage(err: unknown): string | null {
  if (!isAxiosError(err) || err.response?.status !== 403) return null;
  const detail = err.response?.data?.detail;
  return typeof detail === 'string' && detail.toLowerCase().includes('hidden for your role')
    ? detail
    : null;
}

/** Extract a short panel error message from a React Query error. */
export function panelError(error: Error | null): string | null {
  if (!error) return null;
  const hidden = hiddenForRoleMessage(error);
  if (hidden) return hidden;
  if (isAxiosError(error)) {
    if (error.code === 'ERR_NETWORK') return 'Unable to connect.';
    const status = error.response?.status;
    if (status === 401) return 'Session expired.';
    if (status && status >= 500) return 'Service unavailable.';
  }
  return 'Failed to load.';
}

/**
 * Extract a FastAPI-style error detail from a thrown error.
 *
 * Backend responses look like `{ detail: "..." }`; axios wraps them as
 * `err.response.data.detail`. Falls back to the provided default.
 */
export function extractDetailMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string' && detail.length > 0) return detail;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * The server's words for a refused save, in either 422 shape: a sentence (`{"detail": "..."}`, an
 * AppError) or the request schema's list (`{"detail": [{"msg": "Value error, ..."}]}`), each message
 * without pydantic's "Value error, " prefix. Any other failure reads as `extractDetailMessage`.
 */
export function extractRefusalMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (Array.isArray(detail)) {
      const messages = detail
        .map((d) => (typeof d?.msg === 'string' ? d.msg.replace(/^Value error, /, '') : ''))
        .filter((m) => m.length > 0);
      if (messages.length > 0) return messages.join('; ');
    }
  }
  return extractDetailMessage(err, fallback);
}

/** Map raw backend error messages to user-friendly text. */
export function friendlyErrorMessage(raw: string, retryable: boolean): string {
  if (raw.includes('LLM usage limit reached')) {
    return "We've reached our AI usage limit for the current period. Please check back later or contact support.";
  }
  if (raw.includes('LLM service unavailable')) {
    return 'The AI service is temporarily overloaded. Please try again in a moment.';
  }
  if (raw.includes('Query processing failed')) {
    return 'Something went wrong processing your query. Please try rephrasing.';
  }
  if (retryable) {
    return 'Something went wrong. Please try again in a moment.';
  }
  return raw || 'Query failed. Please try again.';
}
